import styled from 'styled-components';
import { space, color, transition } from '@ifixit/primitives';
import { Tabs, TabList, Tab, TabPanels, TabPanel, Badge } from '@chakra-ui/react';

/*
 * This is our custom reusable Chakra Tabs component configured to
 * our site's styling. All you need to do is pass in the necessary
 * parameters to supply your data, and it should work in any React
 * context.
 *
 * The component takes in two parameters:
 *    1. number: activeTab
 *          The tab at this index in the tabs list will be highlighted.
 *    2. Array<TabObject>: tabsData
 *          Data for every tab to be rendered. TabObject interface is
 *          defined below.
 *    3. boolean: fitted
 *          Whether we want the tabs to fill up all available horizontal
 *          space.
 *    4. string: context
 *          This prop does not have any effect in behavior or appearance.
 *          It is simply used to name class selectors of the Tab components
 *          for tests.
 *
 * This component requires the use of styled components because we
 * add a lot of custom styling for active/hover which aren't possible
 * through Chakra style props.
 *
 * Currently we set TabPanel display to None because their functionality
 * has been too limited for our use cases, yet we want to keep them to
 * avoid points off for accessibility.
 */

const MobileOverflowStyles = `
   @media only screen and (max-width: 800px) {
      position: relative;

      &::after {
         content: '';
         position: absolute;
         right: -1px;
         top: 0;
         height: 100%;
         width: 7.5%;
         background: linear-gradient(90deg, transparent 0%, white 100%);
      }
   }
`;

const TabsWrapper = styled.div`
   .chakra-tabs__tab {
      position: relative;
      min-width: 100px;
      flex-shrink: 0;

      &:hover:not(.active) {
         color: ${color.gray[600]};
         border-color: ${color.gray[400]};
      }

      &.active {
         color: ${color.brand[500]};

         &::after {
            content: '';
            position: absolute;
            bottom: -2px;
            width: 100%;
            height: 2px;
            background-color: ${color.brand[500]};
         }
      }
   }

   ${MobileOverflowStyles}
`;

const RoundedTabsWrapper = styled.div`
   .chakra-tabs__tablist {
      gap: 8px;
   }

   .chakra-tabs__tab {
      border-radius: 8px;
      max-height: 32px;
      padding: 8px;
      transition: color ${transition[150]};

      &.active {
         background-color: ${color.gray[200]};
         cursor: default;
      }

      @media (hover: hover) {
         &:hover:not(.active) {
            color: ${color.brand[500]};
         }
      }
   }

   ${MobileOverflowStyles}

   @media only screen and (max-width: 800px) {
      &::after {
         background: linear-gradient(90deg, transparent 0%, var(--background) 100%);
      }
   }
`;

export interface TabObject {
   label: string;
   handler: () => void;
   panel: HTMLElement;
   badge?: number;
}

type PrettyTabsProps = {
   activeTab: number;
   tabsData: TabObject[];
   fitted: boolean;
   context?: string;
   variant?:
      | 'soft-rounded'
      | 'soft-rounded-soft-gutter'
      | 'soft-gutter'
      | 'soft'
      | 'line'
      | 'line-soft'
      | 'line-soft-gutter'
      | 'line-gutter';
   colorScheme?: 'gray' | 'brand';
};

export default function PrettyTabs({
   activeTab,
   tabsData,
   fitted,
   context,
   variant,
   colorScheme,
}: PrettyTabsProps) {
   return variant ? (
      <RoundedTabsWrapper>
         <Tabs
            isLazy
            isFitted={fitted}
            overflowX="auto"
            pb={2}
            index={activeTab}
            variant={variant}
            colorScheme={colorScheme}
         >
            <TabList>{listTabComponents(tabsData, activeTab, context)}</TabList>
            <TabPanels display="none">{listTabPanelComponents(tabsData)}</TabPanels>
         </Tabs>
      </RoundedTabsWrapper>
   ) : (
      <TabsWrapper>
         <Tabs isLazy isFitted={fitted} overflowX="auto" p={1} index={activeTab} variant={variant}>
            <TabList minHeight={space[7]}>
               {listTabComponents(tabsData, activeTab, context)}
            </TabList>
            <TabPanels display="none">{listTabPanelComponents(tabsData)}</TabPanels>
         </Tabs>
      </TabsWrapper>
   );
}

function listTabPanelComponents(tabsData: any) {
   return tabsData.map((tabObject: any) => <TabPanel key={tabObject.label}></TabPanel>);
}

function listTabComponents(tabsData: any, activeTab: any, context: any) {
   return tabsData.map((tabObject: any, index: any) => {
      const badge = tabObject.badge;
      let baseClass = `${context}Tab`;
      baseClass = tabObject.id ? `${tabObject.id} ${baseClass}` : baseClass;
      return (
         <Tab
            className={index === activeTab ? `active ${baseClass}` : baseClass}
            key={index}
            onClick={tabObject.handler}
            id={tabObject.id ? tabObject.id : tabObject.name}
         >
            <div className="flex gap-1">
               {tabObject.label}
               {badge ? (
                  <div className="translate-y-[-10px]">
                     <Badge
                        fontSize="12px"
                        fontWeight="semibold"
                        borderRadius="full"
                        colorScheme="brand"
                        variant="solid"
                        py="0"
                        px="5.25px"
                     >
                        {badge}
                     </Badge>
                  </div>
               ) : null}
            </div>
         </Tab>
      );
   });
}
